import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Sheet from '@amzn/meridian/sheet'
import Row from '@amzn/meridian/row'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small'
import { IStoreSelectionResult, StoreSelectionPageContainer } from './StoreSelectionPageContainer'
import { getNotificationSheetState } from '@atredux/selectors/notificationSelector'
import { getStoreId, getSelectedStoreId } from '@atredux/selectors/storeSelector'
import { NO_CURRENT_STORE } from '@src/constants'
import { isAdminOrStoreManager } from '@api/userAndStoreContext'
import './StoreSelectionSheet.scss'

interface IStoreSelectionSheetProps {
    open: boolean
    onClose?: () => void
    onComplete?: (result?: IStoreSelectionResult) => void
    showSwitchButton?: boolean
    onSwitchStore?: (storeId: string) => void
}

/**
 * StoreSelectionSheet
 *
 * Sheet wrapper for the StoreSelectionPage that displays from the bottom
 * with a scrim overlay. Triggered after login or when user needs to select a store.
 */
export const StoreSelectionSheet: React.FC<IStoreSelectionSheetProps> = ({
  open,
  onClose,
  onComplete,
  showSwitchButton,
  onSwitchStore,
}) => {
    const isNotificationsOpen = useSelector(getNotificationSheetState)
    const storeId = useSelector(getStoreId)
    const selectedStoreId = useSelector(getSelectedStoreId)
    const hasStore = (storeId && storeId !== NO_CURRENT_STORE) || !!selectedStoreId

    const [canSkip, setCanSkip] = useState(false)

    useEffect(() => {
        if (!open) return
        let cancelled = false
        isAdminOrStoreManager().then((result) => {
            if (!cancelled) setCanSkip(result)
        })
        return () => { cancelled = true }
    }, [open])

    const effectiveOnClose = (hasStore || canSkip) ? onClose : undefined

    return (
    <Sheet
      type="overlay"
      side="bottom"
      open={open}
      onClose={effectiveOnClose}
      spacingInset="none"
      data-atb-id="store-selection-sheet"
      // Position-dependent styling must hang off the class: Meridian puts
      // `className` on the outer container it positions, `data-*` on the inner div.
      className={['store-selection-sheet', isNotificationsOpen && 'store-selection-above-notifications'].filter(Boolean).join(' ')}
    >
      <div className="store-selection-sheet-content">
        <StoreSelectionPageContainer
          onComplete={onComplete}
          showSwitchButton={showSwitchButton}
          onSwitchStore={onSwitchStore}
          isSheetOpen={open}
          onClose={effectiveOnClose}
        />
      </div>
    </Sheet>
    )
}

export default StoreSelectionSheet
